import {
  Component,
  EventEmitter,
  ElementRef,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  OnDestroy,
  Input,
  AfterViewInit,
  ChangeDetectorRef,
  AfterViewChecked,
} from '@angular/core';
import { ActionType, ContentStatus, IFileActionProgress } from '../../file-manager/fileData';
import { FileManagerService, UploadContent } from 'xsmodules/remote-control/services/file-manager.service';
import { Subject, Subscription } from 'rxjs';
import { SotiPopupTrigger } from '@soti/popup';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEventBusService } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';

const inProgressTemplate = 'inProgressTemplate';
const doneTemplate = 'doneTemplate';
const failedTemplate = 'failedTemplate';
const errorSeparator = ' - ';
@Component({
  selector: 'app-file-manager-status-center',
  templateUrl: './file-manager-status-center.component.html',
  styleUrls: ['./file-manager-status-center.component.scss'],
})
export class FileManagerStatusCenterComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild(inProgressTemplate, { static: true }) inProgressTemplateRef: TemplateRef<string>;
  @ViewChild(doneTemplate, { static: true }) doneTemplateRef: TemplateRef<string>;
  @ViewChild(failedTemplate, { static: true }) failedTemplateRef: TemplateRef<string>;

  @Input() getPopupTrigger: () => SotiPopupTrigger;
  @Output() tabSelect = new EventEmitter<boolean>();

  public currentTemplate: TemplateRef<string>;
  private fileUploadUpdatedSub: Subscription;
  public displayShadow = false;
  private fileDownloadUpdated: Subscription;

  constructor(
    private saI18nPipe: XsI18nPipe,
    private fileManagerService: FileManagerService,
    private globalEventBusService: GlobalEventBusService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.getStatusCenterData(ContentStatus.Failed).length > 0) {
      this.setStatusCenterTemplate(failedTemplate);
    } else {
      this.setTemplateAndData(this.inProgressTemplateRef);
    }

    // This code will run once the ticket is loaded, so we can finish embedding into the ticket what ever we where doing in standalone
    this.fileUploadUpdatedSub = this.globalEventBusService.register(
      GlobalEvents.fileUploadUpdated,
      this.onFileProgressUpdated
    );
    this.fileDownloadUpdated = this.globalEventBusService.register(
      GlobalEvents.fileDownloadUpdated,
      this.onFileProgressUpdated
    );
  }

  ngAfterViewChecked() {
    this.onScroll();
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.fileUploadUpdatedSub) {
      this.fileUploadUpdatedSub.unsubscribe();
    }
    if (this.fileDownloadUpdated) {
      this.fileDownloadUpdated.unsubscribe();
    }
  }

  onScroll = (): void => {
    // const templateContainer = $('.template-div')[0];
    // if (!templateContainer) {
    //   return;
    // }
    // this.displayShadow = templateContainer.scrollTop >= 40;
  };

  public selectStatusCenterTemplate = (): TemplateRef<string> => {
    this.onScroll();
    return this.currentTemplate || this.inProgressTemplateRef;
  };

  // This function will be called when user clicks X button to cancel upload file or download file action.
  public cancelAction = (statusData: IFileActionProgress): void => {
    this.fileManagerService.cancelFileAction(statusData);
  };

  public clearStatusCenterDataByContentStatus = (contentStatus: ContentStatus[]): void => {
    this.fileManagerService.clearStatusCenterDataByContentStatus(contentStatus);
  };

  public deleteSingleRecordFromStatusCenter = (statusData: IFileActionProgress): void => {
    this.fileManagerService.deleteSingleRecordFromStatusCenter(statusData);
  };

  public setStatusCenterTemplate = (templateRefString: string) => {
    switch (templateRefString) {
      case inProgressTemplate: {
        this.setTemplateAndData(this.inProgressTemplateRef);
        break;
      }
      case doneTemplate: {
        this.setTemplateAndData(this.doneTemplateRef);
        break;
      }
      case failedTemplate: {
        this.setTemplateAndData(this.failedTemplateRef);
        break;
      }
      default: {
        this.setTemplateAndData(this.inProgressTemplateRef);
      }
    }

    // Raise event with value as true when current tab is failed tab else raise value as false.
    this.tabSelect.emit(this.currentTemplate === this.failedTemplateRef);
  };

  public getStatusCenterData = (contentStatus: ContentStatus): IFileActionProgress[] =>
    Array.from(this.fileManagerService.getStatusCenterData().values()).filter(x => x.contentStatus === contentStatus);

  public getStatusCenterDataByStatuses = (contentStatuses: ContentStatus[]): IFileActionProgress[] =>
    Array.from(this.fileManagerService.getStatusCenterData().values()).filter(x =>
      contentStatuses.some(status => x.contentStatus === status)
    );

  public getStatusCenterDataSortedByCompletionDate = (contentStatus: ContentStatus[]): IFileActionProgress[] =>
    Array.from(this.fileManagerService.getStatusCenterData().values())
      .filter(x => contentStatus.some(status => status === x.contentStatus))
      .sort((a, b) => new Date(b.timeOfCompletion).getTime() - new Date(a.timeOfCompletion).getTime());

  public setTemplateAndData = (templateref: TemplateRef<string>) => {
    this.currentTemplate = templateref;
  };

  public getErrorMessage = (actionType: ActionType, errorMessage: string): string => {
    let failedActionType = '';
    if (actionType === 0) {
      failedActionType = this.saI18nPipe.transform('file-manager-status-center-uploadfailed-actiontype');
    } else {
      failedActionType = this.saI18nPipe.transform('file-manager-status-center-downloadfailed-actiontype');
    }
    return failedActionType + errorSeparator + errorMessage;
  };

  public getDoneDescription = (statusData: IFileActionProgress): string => {
    let description = '';

    if (statusData.error) {
      description = statusData.error;
    } else {
      description =
        statusData.actionType === 0
          ? this.saI18nPipe.transform('file-manager-status-center-uploaded-actiontype')
          : this.saI18nPipe.transform('file-manager-status-center-downloaded-actiontype');
    }

    return description;
  };

  public get contentStatusEnum(): typeof ContentStatus {
    return ContentStatus;
  }

  public overwriteAction = (statusData: IFileActionProgress) => {
    const uploadContent = { content: statusData.file, path: '', overwrite: true } as UploadContent;
    const location = statusData.location;
    this.fileManagerService.statusCenterData.delete(statusData.file);
    this.fileManagerService.uploadContent([uploadContent], location);
  };

  public getActionTypeIcon = (actionType: ActionType): string => {
    switch (actionType) {
      case ActionType.Upload:
        return 'hd-icon-upload';
      case ActionType.Download:
      case ActionType.Compress:
        return 'hd-icon-download';
      default:
        return '';
    }
  };

  public getStatusFileName = (statusData: IFileActionProgress): string => {
    return this.saI18nPipe.transform('file-manager-status-center-filename', statusData.file.name);
  }

  public getLocationText = (statusData: IFileActionProgress): string => {
    let result = '';
    if (statusData.actionType === ActionType.Upload) {
      result = this.saI18nPipe.transform('file-manager-status-center-to-text', statusData.location);
    } else if (statusData.actionType === ActionType.Download) {
      result = this.saI18nPipe.transform('file-manager-status-center-from-text', statusData.location);
    } else if (statusData.actionType === ActionType.Compress) {
      const lastIndexOfFolder = statusData.location.lastIndexOf(statusData.file.name);
      const parentFolder = statusData.location.substring(0, lastIndexOfFolder);
      result = this.saI18nPipe.transform('file-manager-status-center-from-text', parentFolder);
    }
    return result;
  };

  public getModeType = (statusData: IFileActionProgress): string => {
    let modeType = 'determinate';

    if (
      statusData &&
      statusData.contentStatus === ContentStatus.CompressInProgress &&
      statusData.actionType === ActionType.Compress
    ) {
      modeType = 'indeterminate';
    }

    return modeType;
  };

  public getActionTypeText = (statusData: IFileActionProgress): string => {
    let actionTypeText = '';
    switch (statusData.actionType) {
      case ActionType.Upload:
        actionTypeText =
          statusData.progress === 0
            ? 'file-manager-status-center-upload-queued-actiontype'
            : 'file-manager-status-center-uploading-actiontype';
        break;
      case ActionType.Download:
        actionTypeText =
          statusData.progress === 0 && statusData.contentStatus === ContentStatus.InProgress
            ? 'file-manager-status-center-download-queued-actiontype'
            : 'file-manager-status-center-downloading-actiontype';
        break;
      case ActionType.Compress:
        actionTypeText =
          statusData.contentStatus === ContentStatus.CompressInProgress
            ? 'file-manager-status-center-compressing-actiontype'
            : 'file-manager-status-center-download-queued-actiontype';
        break;
    }
    return this.saI18nPipe.transform(actionTypeText);
  };

  public closeStatusCenter = (): void => {
    this.getPopupTrigger().close();
  };

  private onFileProgressUpdated = (fileProgressUpdateInfo: IFileActionProgress): void => {
    // When there is an error Uploading or Downloading a file
    // Status Center should be automatically opened on the 'Failed' tab
    if (fileProgressUpdateInfo.error && !fileProgressUpdateInfo.isCancellation) {
      this.setStatusCenterTemplate(failedTemplate);
      this.getPopupTrigger().open();
    }
  };
}
